import { Check } from "lucide-react-native";
import { useState } from "react";
import { Badge, BadgeText } from "../../../../ui/badge";
import { Box } from "../../../../ui/box";
import { Button, ButtonSpinner, ButtonText } from "../../../../ui/button";
import { HStack } from "../../../../ui/hstack";
import { Icon } from "../../../../ui/icon";
import { Text } from "../../../../ui/text";
import { VStack } from "../../../../ui/vstack";
import { AGENT_ACTIONS } from "../../actions";
import { useAgentForm } from "./AgentFormContext";
import type { ConceptCardData } from "./formTypes";

type Props = { data: ConceptCardData };

/**
 * One concept: a title, the line it opens with, and what happens.
 *
 * Three states beyond the plain offer. `stale` — a newer set replaced this one, so it
 * stays in the thread for reference but cannot be chosen. `chosen` — the one that was
 * picked. And, once the session stops offering `select_concept` (a concept was chosen), the others
 * are simply no longer offers.
 *
 * "Elegir" waits for the job that wrote the set to finish: the backend refuses a
 * second job while one runs, so a click before that could only fail.
 */
const ConceptCard = ({ data }: Props) => {
  const { busy, can, selectConcept } = useAgentForm();
  const { concept, stale, chosen } = data;
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const closed = stale || (!chosen && !can(AGENT_ACTIONS.selectConcept));
  const canChoose = !closed && !chosen && !busy && !choosing;

  const choose = async () => {
    if (!canChoose) return;
    setError(null);
    setChoosing(true);
    const result = await selectConcept(concept.id);
    setChoosing(false);
    if (!result.ok) setError(result.errors[0]?.message ?? null);
  };

  return (
    <Box
      role="group"
      accessibilityLabel={`Concepto: ${concept.title}`}
      className={`mb-3 ml-[30px] min-w-0 rounded-2xl border p-4 animate-fade-rise motion-reduce:animate-none ${
        chosen
          ? "border-brand-400 bg-brand-50"
          : "border-outline-100 bg-background-0"
      } ${closed ? "opacity-60" : ""}`}
    >
      <VStack space="sm">
        <HStack space="sm" className="flex-wrap items-center">
          {stale ? (
            <Badge action="muted" variant="outline" size="sm" rounded="md">
              <BadgeText>Basado en un brief anterior</BadgeText>
            </Badge>
          ) : null}
          {chosen ? (
            <Badge action="success" variant="solid" size="sm" rounded="md">
              <Icon
                as={Check}
                size="2xs"
                className="mr-1 text-typography-white"
              />
              <BadgeText>Elegido</BadgeText>
            </Badge>
          ) : null}
          {concept.format ? (
            <Badge action="muted" variant="outline" size="sm" rounded="md">
              <BadgeText>{concept.format}</BadgeText>
            </Badge>
          ) : null}
        </HStack>

        <Text className="text-base font-semibold text-typography-900">
          {concept.title}
        </Text>
        {concept.hook ? (
          <Text className="text-sm italic text-typography-700">
            “{concept.hook}”
          </Text>
        ) : null}
        {concept.description ? (
          <Text className="text-sm text-typography-600">
            {concept.description}
          </Text>
        ) : null}
        {concept.referenceHandle ? (
          <Text className="text-xs text-typography-500">
            Inspirado en {concept.referenceHandle}
          </Text>
        ) : null}

        {error ? (
          <Text role="alert" className="text-sm text-error-600">
            {error}
          </Text>
        ) : null}

        {chosen ? null : (
          <Button
            action="brand"
            variant="solid"
            size="md"
            className="min-h-11 self-start"
            isDisabled={!canChoose}
            onPress={() => void choose()}
          >
            {choosing ? <ButtonSpinner /> : null}
            <ButtonText>Elegir</ButtonText>
          </Button>
        )}
      </VStack>
    </Box>
  );
};

export default ConceptCard;
